import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Privacy Policy',
  sections: [{
    heading: 'Information We Collect',
    body: 'We collect information you provide directly, such as your name and email address when you make a purchase. We also collect basic usage data to improve your experience.'
  }, {
    heading: 'How We Use Your Information',
    body: 'Your information is used solely to deliver your purchased products, send order confirmations, and provide customer support. We never sell your personal data to third parties.'
  }, {
    heading: 'Cookies & Analytics',
    body: 'We use minimal cookies to understand how visitors interact with our site. No personally identifiable information is shared with analytics providers.'
  }, {
    heading: 'Data Security',
    body: 'We use industry-standard encryption and secure payment processing to protect your information. Your payment details are never stored on our servers.'
  }, {
    heading: 'Your Rights',
    body: 'You may request access to, correction of, or deletion of your personal data at any time.'
  }]
};
const termsContent = {
  title: 'Terms of Service',
  sections: [{
    heading: 'Digital Products',
    body: 'All products sold are digital downloads delivered instantly after purchase. By completing your purchase, you agree to these terms.'
  }, {
    heading: 'Usage Rights',
    body: 'Your purchase grants you a personal, non-transferable license to use the materials for your own family. You may not redistribute, resell, or share the content publicly.'
  }, {
    heading: 'Refund Policy',
    body: 'Due to the digital nature of our products, all sales are final. However, if you feel the product did not meet the description provided, we will work with you to find a resolution within 14 days of purchase.'
  }, {
    heading: 'Disclaimer',
    body: "The content provided is for informational and educational purposes only. It is not a substitute for professional medical, psychological, or therapeutic advice. Always consult a qualified professional for concerns about your child's development or behavior."
  }, {
    heading: 'Limitation of Liability',
    body: 'We are not liable for any outcomes resulting from the use of our materials. Results may vary based on individual circumstances.'
  }, {
    heading: 'Changes to Terms',
    body: 'We reserve the right to update these terms at any time. Continued use of our products constitutes acceptance of any changes.'
  }]
};
export function FooterSection() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-10 bg-cream-100 border-t border-sage-light/20" data-id="element-44">
        <div className="max-w-4xl mx-auto px-4 flex flex-col items-center gap-4" data-id="element-45">
          <div className="flex items-center gap-6" data-id="element-46">
            <button onClick={() => setActiveModal('privacy')} className="text-sage-dark/50 hover:text-terracotta transition-colors text-sm font-medium underline underline-offset-2 decoration-sage-light/40 hover:decoration-terracotta" data-id="element-47">
              Privacy Policy
            </button>
            <span className="text-sage-light/40" data-id="element-48">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-sage-dark/50 hover:text-terracotta transition-colors text-sm font-medium underline underline-offset-2 decoration-sage-light/40 hover:decoration-terracotta" data-id="element-49">
              Terms of Service
            </button>
          </div>
          <p className="text-sage-dark/40 text-sm" data-id="element-50">
            © 2026 After-School Evenings Without the Meltdowns. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-51">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-52">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-sage-dark/40 backdrop-blur-sm" data-id="element-53" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-cream-100 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-sage-light/30" data-id="element-54">
              {/* Header */}
              <div className="sticky top-0 bg-cream-100 border-b border-sage-light/20 px-8 py-5 flex items-center justify-between z-10" data-id="element-55">
                <h2 className="text-2xl font-bold font-serif text-sage-dark" data-id="element-56">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="w-9 h-9 rounded-full bg-sage-light/20 flex items-center justify-center text-sage-dark/60 hover:bg-terracotta/10 hover:text-terracotta transition-colors" data-id="element-57">
                  <X size={18} data-id="element-58" />
                </button>
              </div>

              {/* Body */}
              <div className="px-8 py-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-59">
                {content.sections.map((section, i) => <div key={i} data-id="element-60">
                    <h3 className="text-lg font-bold text-sage-dark mb-2" data-id="element-61">
                      {section.heading}
                    </h3>
                    <p className="text-sage-dark/70 leading-relaxed" data-id="element-62">
                      {section.body}
                    </p>
                  </div>)}

                <div className="pt-4 border-t border-sage-light/20" data-id="element-63">
                  <p className="text-sage-dark/40 text-xs" data-id="element-64">
                    Last updated: February 2026
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}